import { useState, useCallback } from 'react';
import { useInView } from '@/hooks/useInView';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Clock, Globe } from 'lucide-react';
import axios from 'axios';
import { useDynamicContent, usePublicSeo, usePublicSettings } from '@/hooks/useDynamicContent';
import Seo from '@/seo/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABSTRACT_IMG = 'https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBnb2xkJTIwbGluZXN8ZW58MHx8fHwxNzc1OTU2NDg2fDA&ixlib=rb-4.1.0&q=85';

const DEFAULTS = {
  hero_title: "Let's Support",
  hero_title_gold: 'Your Mission',
  hero_description: "If you have a technology support need, an operational challenge, or just want to learn more about how we work, we'd like to hear from you.",
  why_contact_heading: 'Why Contact Us?',
  why_contact_items: [
    'Free initial consultation',
    'Rapid response within 24 hours',
    'Tailored solutions for your mission',
    'No obligation assessment',
  ],
  business_hours_heading: 'Business Hours',
  business_hours: [
    { label: 'Monday - Friday', value: '8:00 AM - 4:00 PM EST' },
    { label: 'Saturday - Sunday', value: 'Closed' },
  ],
  urgent_heading: 'Need immediate assistance?',
  urgent_text: 'For urgent project inquiries, call us directly during business hours.',
};

const DEFAULT_SETTINGS = {
  email: 'contact@scoreintegratedsolutions.com',
  phone: '(256) 427-9202',
  phone_tel: '+12564279202',
  business_address: 'Richmond, VA 23294',
  website: 'www.scoreintegratedsolutions.com',
};

const INITIAL_FORM = { name: '', email: '', company: '', message: '' };

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${className} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </section>
  );
}

function ContactInfoCard({ item }) {
  return (
    <div className={`flex items-center gap-4 p-5 border border-white/5 bg-[#121212] group hover:border-[#F2B705]/20 transition-all ${item.isEmail ? 'min-w-[320px]' : ''}`}>
      <div className="w-10 h-10 border border-[#F2B705]/30 flex items-center justify-center shrink-0 group-hover:border-[#F2B705] transition-colors">
        <item.icon className="text-[#F2B705]" size={16} strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-[#525252] font-bold">{item.label}</p>
        {item.href ? (
          <a href={item.href} className="text-sm text-white hover:text-[#F2B705] transition-colors" style={item.isEmail ? { whiteSpace: 'nowrap', overflow: 'visible' } : {}}>{item.value}</a>
        ) : (
          <p className="text-sm text-white">{item.value}</p>
        )}
      </div>
    </div>
  );
}

function FormStatusMessage({ status }) {
  if (status === 'success') {
    return (
      <div data-testid="contact-success-msg" className="flex items-center gap-2 text-[#F2B705] text-sm mt-4">
        <CheckCircle size={16} /> Message sent successfully. We'll be in touch shortly.
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div data-testid="contact-error-msg" className="flex items-center gap-2 text-red-400 text-sm mt-4">
        <AlertCircle size={16} /> Something went wrong. Please try again.
      </div>
    );
  }
  return null;
}

export default function ContactPage() {
  const c = useDynamicContent('contact', DEFAULTS);
  const seoOverrides = usePublicSeo('contact');
  const settings = usePublicSettings(DEFAULT_SETTINGS);

  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.message) return;
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(INITIAL_FORM);
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const phoneTel = settings.phone_tel || `+1${(settings.phone || '').replace(/[^0-9]/g, '')}`;
  const businessHoursLine = (c.business_hours || []).find((b) => b.label?.toLowerCase().includes('mon'))?.value || 'Mon-Fri 8AM-4PM EST';

  const contactInfo = [
    { icon: Mail, label: 'Email', value: settings.email, href: `mailto:${settings.email}`, isEmail: true },
    { icon: Phone, label: 'Phone', value: settings.phone, href: `tel:${phoneTel}` },
    { icon: MapPin, label: 'Location', value: settings.business_address, href: null },
    { icon: Clock, label: 'Hours', value: businessHoursLine, href: null },
  ];

  return (
    <div className="min-h-screen bg-[#121212]">
      <Seo
        page="contact"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <section data-testid="contact-hero" className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ABSTRACT_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/60 via-[#121212]/85 to-[#121212]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-4 border border-[#F2B705]/30 px-4 py-1.5">Get In Touch</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            {c.hero_title}<br />
            <span className="gold-gradient-text">{c.hero_title_gold}</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed">{c.hero_description}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* Quick Info Cards */}
      <Section className="py-12 bg-[#161616]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid gap-4" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr' }}>
            {contactInfo.map((item) => (
              <ContactInfoCard key={item.label} item={item} />
            ))}
          </div>
        </div>
      </Section>

      {/* Form & Details */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Send a Message</span>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 block">Full Name *</Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} required data-testid="contact-name-input" placeholder="Your full name" className="bg-transparent border-white/10 text-white placeholder:text-[#525252] focus:border-[#F2B705] focus:ring-[#F2B705] h-12" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 block">Email *</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} required data-testid="contact-email-input" placeholder="you@company.com" className="bg-transparent border-white/10 text-white placeholder:text-[#525252] focus:border-[#F2B705] focus:ring-[#F2B705] h-12" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company" className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 block">Company / Organization *</Label>
                  <Input id="company" name="company" value={form.company} onChange={handleChange} required data-testid="contact-company-input" placeholder="Your organization" className="bg-transparent border-white/10 text-white placeholder:text-[#525252] focus:border-[#F2B705] focus:ring-[#F2B705] h-12" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold mb-2 block">Message *</Label>
                  <Textarea id="message" name="message" value={form.message} onChange={handleChange} required rows={5} data-testid="contact-message-input" placeholder="Tell us about your project or requirements..." className="bg-transparent border-white/10 text-white placeholder:text-[#525252] focus:border-[#F2B705] focus:ring-[#F2B705] resize-none" />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-10 py-4 hover:bg-[#D9A404] transition-colors flex items-center gap-2 disabled:opacity-50">
                  {loading ? 'Sending...' : 'Send Message'} <Send size={16} />
                </button>
                <FormStatusMessage status={status} />
              </form>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-[#161616] border border-white/10 p-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F2B705] to-transparent" />
                <h3 className="text-lg font-semibold text-white mb-4">{c.why_contact_heading}</h3>
                <ul className="space-y-3">
                  {(c.why_contact_items || []).map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle size={14} className="text-[#F2B705] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#A3A3A3]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#161616] border border-white/10 p-8">
                <h3 className="text-sm font-semibold text-white mb-4">{c.business_hours_heading}</h3>
                <div className="space-y-3 text-sm">
                  {(c.business_hours || []).map((bh) => (
                    <div key={bh.label} className="flex justify-between">
                      <span className="text-[#A3A3A3]">{bh.label}</span>
                      <span className={bh.value?.toLowerCase().includes('closed') ? 'text-[#525252]' : 'text-white'}>{bh.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-8 border border-[#F2B705]/20 bg-[#F2B705]/5 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#F2B705]" />
                <p className="text-sm font-semibold text-[#F2B705] mb-2">{c.urgent_heading}</p>
                <p className="text-xs text-[#A3A3A3] leading-relaxed mb-3">{c.urgent_text}</p>
                <a href={`tel:${phoneTel}`} className="text-sm text-white font-bold hover:text-[#F2B705] transition-colors">{settings.phone}</a>
              </div>

              <div className="flex items-center gap-3 p-6 border border-white/5 bg-[#121212]">
                <Globe className="text-[#F2B705]" size={18} />
                <a href={`https://${settings.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#A3A3A3] hover:text-[#F2B705] transition-colors">
                  {settings.website}
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
